

import { getCDNBaseUrl } from '../config/environment';

/**
 * GameQuestionManager - CDN-based question loading system
 * 
 * Features:
 * - Loads 10 unique, non-sequential random questions initially
 * - Prefetches next set at Q8 for optimal performance
 * - Ensures category diversity and answer option balance
 * - Manages uniqueness across sessions
 * - Handles all game types (Trivia, Would You Rather, etc.)
 * - Optimized for Cloudflare CDN with caching
 */

export interface Question {
  id: number;
  category: string;
  question?: string;
  option1?: string;
  option2?: string;
  options?: string[];
  correctIndex?: number;
  type?: 'truth' | 'dare';
  funFact?: string;
}

export interface CDNBlock {
  questions: Question[];
  totalQuestions: number;
  blockNumber: number;
}

export interface CDNIndex {
  totalBlocks: number;
  totalQuestions: number;
  blockSize: number;
  lastUpdated: string;
}

export interface GameConfig {
  gameType: 'trivia' | 'wouldYouRather' | 'whichOneYouAre' | 'truth' | 'dare';
  language: 'en' | 'es' | 'fr' | 'hi' | 'de';
  season: 'v1' | 'v2';
  questionsPerSet: number;
  prefetchAtQuestion: number;
  // Optional user ID to seed diversity across users
  userId?: string | null;
}

export class GameQuestionManager {
  private config: GameConfig;
  private persistence?: import('./QuestionProgressPersistence').QuestionProgressPersistence;
  private currentQuestions: Question[] = [];
  private usedQuestionIds: Set<string> = new Set();
  private prefetchedQuestions: Question[] = [];
  private prefetchedBlockNumber: number | null = null;
  // emergency fallback logic removed in production
  private currentBlockNumber: number = 0;
  private totalBlocks: number = 0;
  private totalQuestions: number = 0;
  private isLoading: boolean = false;
  private cdnBaseUrl: string;
  private isDevelopment: boolean = process.env.NODE_ENV === 'development';
  private progressSaveTimer: ReturnType<typeof setTimeout> | null = null;

  // Session-level randomness (does not affect persisted cursor)
  private sessionSeed: number = 0;
  private sessionRng: (() => number) | null = null;
  private sessionStride: number = 1;
  private invalidBlocks: Set<number> = new Set();
  private notifiedBlocks: Set<number> = new Set();
  private verbose: boolean = false;

  private debugState(context: string): void {
    if (!this.verbose) return;
    try {
      console.log('[QMAN]', context, {
        gameType: this.config.gameType,
        language: this.config.language,
        season: this.config.season,
        isDevelopment: this.isDevelopment,
        currentBlockNumber: this.currentBlockNumber,
        totalBlocks: this.totalBlocks,
        totalQuestions: this.totalQuestions,
        usedQuestionCount: this.usedQuestionIds.size,
        hasPrefetched: this.prefetchedQuestions.length > 0,
        persistence: this.persistence ? 'enabled' : 'disabled'
      });
    } catch {
      // ignore debug errors
    }
  }



  constructor(config: GameConfig, persistence?: import('./QuestionProgressPersistence').QuestionProgressPersistence) {
    this.config = config;
    this.persistence = persistence;
    this.cdnBaseUrl = getCDNBaseUrl();
    
    // Only handle verbose logging, no more conditional CDN logic
    let envVerbose = false;
    try {
      // Support Vite and Node-style envs
      const viteVerbose = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_QMAN_VERBOSE) || undefined;
      const nodeVerbose = (process as any)?.env?.VITE_QMAN_VERBOSE;
      envVerbose = (viteVerbose === 'true') || (nodeVerbose === 'true');
    } catch (_) {
      // ignore
    }
    
    // Runtime verbose flag via query param or localStorage
    let runtimeVerbose = false;
    try {
      const qp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const qpVerbose = qp ? (qp.get('qman_verbose') === '1') : false;
      const lsVerbose = typeof localStorage !== 'undefined' ? (localStorage.getItem('QMAN_VERBOSE') === '1') : false;
      runtimeVerbose = Boolean(qpVerbose || lsVerbose);
    } catch (_) {
      // ignore
    }
    

    this.verbose = Boolean(envVerbose || runtimeVerbose);
    
    // Initialize session RNG using userId + timestamp to diversify sessions
    const seedBasis = `${config.userId || ''}|${Date.now()}`;
    this.initSessionRng(seedBasis);
    this.loadUsedQuestionIds();
    this.debugState('constructor: initialized');
  }

  /**
   * Initialize the question manager and load first set
   */
  async initialize(): Promise<Question[]> {
    try {
      console.log(`🎮 Initializing GameQuestionManager for ${this.config.gameType} ${this.config.language}`);
      console.log(`🔍 Environment: NODE_ENV = ${process.env.NODE_ENV}`);
      console.log(`🔍 isDevelopment = ${this.isDevelopment}`);
      this.debugState('initialize: start');
      
      console.log('🌐 Loading from CDN... (DATASET=CDN)');
      
      // Add timeout for CDN loading
      const cdnTimeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('CDN loading timeout')), 30000); // 30 second timeout
      });
      
      try {
        // Load CDN index to get total blocks
        await Promise.race([this.loadCDNIndex(), cdnTimeout]);
        await Promise.race([this.loadRemoteProgress(), cdnTimeout]);
        
        // Load first set of questions
        return await Promise.race([this.loadNextQuestionSet(), cdnTimeout]);
      } catch (timeoutError) {
        console.warn('⚠️ CDN loading timed out');
        console.error('❌ CDN loading failed, cannot fallback');
        throw new Error(`CDN loading timeout: ${timeoutError instanceof Error ? timeoutError.message : String(timeoutError)}`);
      }
      
      // Don't prefetch during initialization - only prefetch when we reach the threshold
      // this.prefetchNextSet();
      
      this.debugState('initialize: success');
    } catch (error) {
      console.error('❌ Failed to initialize GameQuestionManager (DATASET_LOADING_FAILED):', error);
      this.debugState('initialize: failed');
      
      // Always throw error - no fallbacks
      throw error;
    }
  }

  /**
   * Get next question from current set
   */
  getNextQuestion(): Question | null {
    if (this.currentQuestions.length === 0) {
      console.log('⚠️ No questions available in current set');
      return null;
    }

    const question = this.currentQuestions.shift()!;
    
    // Mark as used (ensure ID is string)
    this.usedQuestionIds.add(String(question.id));
    this.saveUsedQuestionIds();
    this.scheduleSaveProgress();

    // Check if we need to prefetch next set (when 2 questions remain in 10-question block)
    if (this.currentQuestions.length === 2) {
      console.log(`🚀 Triggering prefetch at question ${this.currentQuestions.length} (2 questions remaining)`);
      this.prefetchNextSet().catch(error => {
        console.error('❌ Prefetch failed:', error);
      });
    }

    // If we're running low, load next set
    if (this.currentQuestions.length === 0) {
      console.log('🔄 No more questions available, loading next set...');
      this.loadNextQuestionSet().catch(error => {
        console.error('❌ Failed to load next question set:', error);
      });
    }

    return question;
  }

  /**
   * Get current question without removing it
   */
  getCurrentQuestion(): Question | null {
    return this.currentQuestions[0] || null;
  }

  /**
   * Get remaining questions count
   */
  getRemainingQuestions(): number {
    return this.currentQuestions.length;
  }

  /**
   * Check if manager is initialized
   */
  isInitialized(): boolean {
    return this.currentQuestions.length > 0 || this.prefetchedQuestions.length > 0;
  }

  /**
   * Load next set of questions from CDN
   */
  private async loadNextQuestionSet(): Promise<Question[]> {
    if (this.isLoading) {
      console.log('⏳ Already loading questions, waiting...');
      return this.currentQuestions;
    }

    this.isLoading = true;

    try {
      this.debugState('loadNextQuestionSet: begin');
      // Use prefetched questions if available
      if (this.prefetchedQuestions.length > 0) {
        console.log('📦 Using prefetched questions');
        this.currentQuestions = this.prefetchedQuestions;
        this.prefetchedQuestions = [];
        if (this.prefetchedBlockNumber) {
          this.currentBlockNumber = this.prefetchedBlockNumber;
          this.prefetchedBlockNumber = null;
        }
        this.isLoading = false;
        this.debugState('loadNextQuestionSet: used prefetched');
        return this.currentQuestions;
      }

      

      // Try sequentially across blocks until we find available questions
      let nextBlockNumber = this.getNextBlockNumber();
      let attempts = 0;
      while (attempts < Math.max(1, this.totalBlocks)) {
        const block = await this.tryLoadCDNBlock(nextBlockNumber);
        if (!block) {
          this.invalidBlocks.add(nextBlockNumber);
          console.warn(`[QMAN] Skipping invalid/missing block ${nextBlockNumber}`);
          nextBlockNumber = (nextBlockNumber % Math.max(1, this.totalBlocks)) + 1;
          attempts += 1;
          continue;
        }
        const availableQuestions = block.questions.filter(q => !this.usedQuestionIds.has(String(q.id)));
        console.log(`[QMAN] block ${nextBlockNumber}: total=${block.questions.length}, available=${availableQuestions.length}, usedIds=${this.usedQuestionIds.size}`);
        
        // Debug: Show which questions are being filtered out
        if (availableQuestions.length < block.questions.length && this.verbose) {
          const usedInThisBlock = block.questions.filter(q => this.usedQuestionIds.has(String(q.id)));
          console.log(`🔍 Block ${nextBlockNumber} debug: ${usedInThisBlock.length} questions already used:`, usedInThisBlock.map(q => q.id));
        }
        
        if (availableQuestions.length > 0) {
          // Ensure we get the target number of questions by loading more blocks if needed
          let allAvailableQuestions = [...availableQuestions];
          let currentBlock = nextBlockNumber;
          let attempts = 0;
          
          // Keep loading blocks until we have enough questions or exhaust all blocks
          while (allAvailableQuestions.length < this.config.questionsPerSet && attempts < this.totalBlocks) {
            currentBlock = (currentBlock % this.totalBlocks) + 1;
            attempts++;
            
            const additionalBlock = await this.tryLoadCDNBlock(currentBlock);
            if (additionalBlock) {
              const additionalQuestions = additionalBlock.questions.filter(q => !this.usedQuestionIds.has(String(q.id)));
              allAvailableQuestions.push(...additionalQuestions);
              console.log(`📦 Added ${additionalQuestions.length} questions from block ${currentBlock}, total available: ${allAvailableQuestions.length}`);
            }
          }
          
          const selectedQuestions = this.selectDiverseQuestions(allAvailableQuestions);
          this.currentQuestions = selectedQuestions;
          // Commit the actual block we used for this set
          this.currentBlockNumber = nextBlockNumber;
          console.log(`✅ Loaded ${selectedQuestions.length} questions from ${attempts + 1} blocks, starting with block ${this.currentBlockNumber}`);
          console.log(`🔧 Setting currentBlockNumber to ${this.currentBlockNumber}`);
          this.debugState('loadNextQuestionSet: success with block');
          this.scheduleSaveProgress();
          return selectedQuestions;
        }

        // Advance to next block
        nextBlockNumber = (nextBlockNumber % Math.max(1, this.totalBlocks)) + 1;
        attempts += 1;
      }

      // If we reached here, we found no available questions in any block
      const totalKnown = this.totalQuestions || this.usedQuestionIds.size;
      console.log(`[QMAN] No available questions in any block. used=${this.usedQuestionIds.size}, totalKnown=${totalKnown}`);
      if (this.usedQuestionIds.size >= totalKnown) {
        console.log('🔄 All CDN questions used across all blocks, resetting used questions');
        this.usedQuestionIds.clear();
        this.saveUsedQuestionIds();
        // Reset cursor and try again
        this.currentBlockNumber = 0;
        console.log(`🔧 Reset currentBlockNumber to 0 (all questions used)`);
        const result = await this.loadNextQuestionSet();
        this.scheduleSaveProgress();
        return result;
      }

      // Fallback: reset cursor and try again
      console.log('⚠️ No available questions found despite not exhausting totals. Resetting cursor and retrying.');
      this.currentBlockNumber = 0;
      console.log(`🔧 Reset currentBlockNumber to 0 (fallback)`);
      const result = await this.loadNextQuestionSet();
      this.scheduleSaveProgress();
      return result;
         } catch (error) {
       console.error('❌ Failed to load question set (DATASET_LOADING_FAILED):', error);
       this.debugState('loadNextQuestionSet: failed');
       
               // Always throw error - no fallbacks
       this.isLoading = false;
       throw new Error(`Failed to load questions: ${error instanceof Error ? error.message : String(error)}`);
     } finally {
      this.isLoading = false;
    }
  }

  /**
   * Create a single emergency fallback question for temporary serving
   */
  // getEmergencyFallbackQuestion removed; no fallbacks in production path

  /**
   * Prefetch next set of questions for optimal performance
   */
  private async prefetchNextSet(): Promise<void> {
    console.log(`🔍 Prefetch check: isLoading=${this.isLoading}, prefetchedQuestions=${this.prefetchedQuestions.length}, usedIds=${this.usedQuestionIds.size}`);
    if (this.isLoading || this.prefetchedQuestions.length > 0) {
      console.log(`⏭️ Skipping prefetch: isLoading=${this.isLoading}, prefetchedQuestions=${this.prefetchedQuestions.length}`);
      return;
    }

    try {
      console.log('🚀 Prefetching next question set...');
      
      // Scan ahead to find a block with available questions
      let nextBlockNumber = this.getNextBlockNumber();
      console.log(`🎯 Prefetch starting with block ${nextBlockNumber}`);
      let attempts = 0;
      while (attempts < Math.max(1, this.totalBlocks)) {
        console.log(`[QMAN] prefetch: attempt=${attempts + 1} nextBlock=${nextBlockNumber}`);
        const block = await this.tryLoadCDNBlock(nextBlockNumber);
        if (!block) {
          this.invalidBlocks.add(nextBlockNumber);
          console.warn(`[QMAN] Skipping invalid/missing block ${nextBlockNumber}`);
          nextBlockNumber = (nextBlockNumber % Math.max(1, this.totalBlocks)) + 1;
          attempts += 1;
          continue;
        }
        const availableQuestions = block.questions.filter(q => !this.usedQuestionIds.has(String(q.id)));
        console.log(`[QMAN] prefetch block ${nextBlockNumber}: total=${block.questions.length}, available=${availableQuestions.length}`);
        
        // Debug: Show which questions are being filtered out
        if (availableQuestions.length < block.questions.length && this.verbose) {
          const usedInThisBlock = block.questions.filter(q => this.usedQuestionIds.has(String(q.id)));
          console.log(`🔍 Block ${nextBlockNumber} debug: ${usedInThisBlock.length} questions already used:`, usedInThisBlock.map(q => q.id));
        }
        
        if (availableQuestions.length > 0) {
          const selectedQuestions = this.selectDiverseQuestions(availableQuestions);
          this.prefetchedQuestions = selectedQuestions;
          this.prefetchedBlockNumber = nextBlockNumber;
          console.log(`📦 Prefetched ${selectedQuestions.length} questions from block ${nextBlockNumber}`);
          console.log(`🎯 Prefetch block number set to: ${this.prefetchedBlockNumber}`);
          this.debugState('prefetch: success');
          return;
        }
        nextBlockNumber = (nextBlockNumber % Math.max(1, this.totalBlocks)) + 1;
        attempts += 1;
      }
      console.log('ℹ️ Prefetch: no available questions found to prefetch');
      console.log(`🔍 Prefetch debug: totalBlocks=${this.totalBlocks}, currentBlock=${this.currentBlockNumber}, usedIds=${this.usedQuestionIds.size}`);
      
      // If we can't find any new questions, maybe we've exhausted all questions
      const totalKnown = this.totalQuestions || this.usedQuestionIds.size;
      if (this.usedQuestionIds.size >= totalKnown * 0.9) { // If we've used 90% of questions
        console.log('🔄 Prefetch: Most questions used, resetting used questions for fresh start');
        this.usedQuestionIds.clear();
        this.saveUsedQuestionIds();
        // Don't retry prefetch here to avoid infinite recursion
        // The next getNextQuestion() call will trigger loadNextQuestionSet() which will find fresh questions
      }
    } catch (error) {
      console.warn('⚠️ Failed to prefetch questions:', error);
      // Don't throw error for prefetch failures
    }
  }

  /**
   * Load CDN index to get total blocks
   */
  private async loadCDNIndex(): Promise<void> {
    const url = `${this.cdnBaseUrl}/${this.config.gameType}/index.json?v=${Date.now()}`;
    console.log(`🔗 Attempting to fetch CDN index from: ${url}`);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(url, {
        cache: 'no-cache', // Force fresh fetch to avoid stale cache
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      console.log(`📡 CDN Index response status: ${response.status} ${response.statusText}`);
      if (this.verbose) {
        console.log(`📡 CDN Index response headers:`, Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const mainIndex = await response.json();
      
      // Find the language-specific index
      const languageInfo = mainIndex.languages?.find((lang: any) => lang.code === this.config.language);
      if (!languageInfo) {
        throw new Error(`Language ${this.config.language} not found in CDN index`);
      }
      
      // Load the language-specific index
      const langIndexUrl = `${this.cdnBaseUrl}/${this.config.gameType}/${languageInfo.indexFile}?v=${Date.now()}`;
      console.log(`🔗 Loading language-specific index from: ${langIndexUrl}`);
      
      const langController = new AbortController();
      const langTimeoutId = setTimeout(() => langController.abort(), 10000); // 10 second timeout
      
      const langResponse = await fetch(langIndexUrl, {
        cache: 'no-cache', // Force fresh fetch to avoid stale cache
        headers: { 'Accept': 'application/json' },
        signal: langController.signal
      });
      
      clearTimeout(langTimeoutId);
      
      if (!langResponse.ok) {
        throw new Error(`Failed to load language index: HTTP ${langResponse.status}`);
      }
      
      const langIndex = await langResponse.json();
      
      // Set the correct block count for this language
      this.totalBlocks = langIndex.totalBlocks;
      this.totalQuestions = langIndex.totalQuestions;
      
      // Initialize session RNG for randomization
      const seedText = `${this.config.userId || 'anonymous'}|${Date.now()}`;
      this.initSessionRng(seedText);
      console.log(`🎲 Session RNG initialized with seed: ${seedText}`);
      
      // Compute a session stride for block traversal diversity (co-prime with totalBlocks)
      this.sessionStride = this.computeStrideFromSeed(this.totalBlocks);
      console.log(`🎯🎯🎯 SESSION STRIDE CALCULATED! Stride: ${this.sessionStride}, Total Blocks: ${this.totalBlocks}`);
      
      // Handle content update for anonymous users (local cleanup)
      this.handleIndexLastUpdated(langIndex.generatedAt || mainIndex.generatedAt);
      
      console.log(`📊 CDN Index loaded: ${this.totalBlocks} blocks, ${this.totalQuestions} total questions`);
      if (this.verbose) {
        console.log(`📊 CDN Index data:`, langIndex);
      }
      this.debugState('loadCDNIndex: success');
    } catch (error) {
      console.error('❌ Failed to load CDN index (DATASET_LOADING_FAILED):', error);
      console.error('🔍 CDN Index failure details:', {
        gameType: this.config.gameType,
        language: this.config.language,
        cdnBaseUrl: this.cdnBaseUrl,
        mainIndexUrl: `${this.cdnBaseUrl}/${this.config.gameType}/index.json?v=${Date.now()}`
      });
      throw new Error(`Failed to load CDN index: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private getProgressKey(): string {
    return `${this.config.gameType}_${this.config.season}_${this.config.language}`;
  }

  private async loadRemoteProgress(): Promise<void> {
    if (!this.persistence) return;
    try {
      const progress = await this.persistence.load(this.getProgressKey());
      if (progress) {
        for (const id of progress.usedQuestionIds || []) {
          this.usedQuestionIds.add(String(id));
        }
        if (typeof progress.currentBlockNumber === 'number' && progress.currentBlockNumber > 0) {
          this.currentBlockNumber = progress.currentBlockNumber;
        }
        this.saveUsedQuestionIds();
      } else {
        // No remote progress: seed an initial block for diversity across users
        if (this.totalBlocks > 0) {
          const seededStart = this.computeSeededStartBlock();
          this.currentBlockNumber = seededStart;
          console.log(`🎲 No remote progress found, starting with random block: ${seededStart}`);
          // Persist initial cursor so devices stay in sync
          await this.saveRemoteProgress();
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to load remote progress:', error);
    }
  }

  private computeSeededStartBlock(): number {
    if (this.totalBlocks <= 0) return 1;
    
    // Add timestamp to ensure better randomness across sessions
    const timestamp = Date.now();
    const basis = `${this.config.userId || ''}|${this.config.gameType}|${this.config.season}|${this.config.language}|${timestamp}`;
    
    let hash = 5381;
    for (let i = 0; i < basis.length; i++) {
      hash = ((hash << 5) + hash) + basis.charCodeAt(i);
      hash |= 0; // force 32-bit
    }
    
    const positive = Math.abs(hash);
    const offset = (positive % this.totalBlocks);
    const result = offset + 1; // 1..totalBlocks
    
    console.log(`🎲 Seeded block calculation: basis="${basis}", hash=${hash}, result=${result}`);
    return result;
  }

  private async saveRemoteProgress(): Promise<void> {
    if (!this.persistence) return;
    
    // Don't save if userId is invalid or empty
    if (!this.config.userId || this.config.userId.trim() === '') {
      console.debug('🔒 Skipping Firestore save - no valid userId');
      return;
    }
    
    const progress: import('./QuestionProgressPersistence').QuestionProgress = {
      usedQuestionIds: Array.from(this.usedQuestionIds).map(id => Number(id)),
      currentBlockNumber: this.currentBlockNumber,
      lastUpdated: new Date().toISOString()
    };
    try {
      await this.persistence.save(this.getProgressKey(), progress);
    } catch (error) {
      console.warn('⚠️ Failed to save remote progress:', error);
    }
  }

  private scheduleSaveProgress(): void {
    if (!this.persistence) return;
    
    // Don't schedule saves if userId is invalid or empty
    if (!this.config.userId || this.config.userId.trim() === '') {
      console.debug('🔒 Skipping Firestore save scheduling - no valid userId');
      return;
    }
    
    if (this.progressSaveTimer) {
      clearTimeout(this.progressSaveTimer);
    }
    this.progressSaveTimer = setTimeout(() => {
      this.saveRemoteProgress();
    }, 400);
  }

  private initSessionRng(seedText: string): void {
    // xorshift32 seeded from a hash of seedText
    let h = 2166136261;
    for (let i = 0; i < seedText.length; i++) {
      h ^= seedText.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    if (h === 0) h = 123456789;
    this.sessionSeed = h >>> 0;
    let x = this.sessionSeed;
    this.sessionRng = () => {
      // xorshift32
      x ^= x << 13;
      x ^= x >>> 17;
      x ^= x << 5;
      x >>>= 0;
      return (x % 1000000) / 1000000; // 0..1
    };
    
    if (this.verbose) {
      console.log(`🎲🎲🎲 SESSION RNG INITIALIZED! Seed: ${seedText}, Hash: ${this.sessionSeed}`);
    }
  }

  private computeStrideFromSeed(total: number): number {
    if (total <= 1) return 1;
    
    // Use a more complex calculation to avoid stride=1 too frequently
    const timestamp = Date.now();
    const combinedSeed = this.sessionSeed + timestamp;
    const base = (combinedSeed % (total - 1)) + 1; // 1..total-1
    
    // Ensure we don't get stride=1 too often (which causes sequential selection)
    if (base === 1 && total > 10) {
      // For larger block counts, prefer a larger stride to spread out selection
      const alternativeStride = Math.max(2, Math.floor(total / 20)); // At least 2, or total/20
      if (this.verbose) console.log(`🎲 Avoiding stride=1, using alternative stride=${alternativeStride}`);
      return alternativeStride;
    }
    
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    if (gcd(base, total) === 1) {
      if (this.verbose) console.log(`🎲 Using stride=${base} (co-prime with ${total})`);
      return base;
    }
    
    // find next co-prime
    for (let s = base + 1; s < total; s++) {
      if (gcd(s, total) === 1) {
        if (this.verbose) console.log(`🎲 Found co-prime stride=${s} (original=${base})`);
        return s;
      }
    }
    
    // fallback - use a reasonable stride
    const fallbackStride = Math.max(2, Math.floor(total / 10));
    if (this.verbose) console.log(`🎲 Using fallback stride=${fallbackStride}`);
    return fallbackStride;
  }

  /**
   * Load specific block from CDN
   */
  // Direct loader retained for manual calls
  private async loadCDNBlock(blockNumber?: number): Promise<CDNBlock> {
    const targetBlock = blockNumber ?? this.getNextBlockNumber();
    const url = `${this.cdnBaseUrl}/${this.config.gameType}/${this.config.gameType}-${this.config.season}-${this.config.language}-block-${targetBlock}.json?v=${Date.now()}`;
    console.log(`🔗 Attempting to fetch CDN block from: ${url}`);
    
    try {
      const response = await fetch(url, {
        cache: 'no-cache', // Force fresh fetch to avoid stale cache
        headers: {
          'Accept': 'application/json'
        }
      });

      console.log(`📡 CDN Block response status: ${response.status} ${response.statusText}`);
      if (this.verbose) {
        console.log(`📡 CDN Block response headers:`, Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const block: CDNBlock = await response.json();
      this.currentBlockNumber = targetBlock;
      
      console.log(`📦 Loaded block ${targetBlock}: ${block.questions.length} questions`);
      if (this.verbose) {
        console.log(`📦 Block data sample:`, block.questions.slice(0, 2));
      }
      this.debugState(`loadCDNBlock: success for block ${targetBlock}`);
      return block;
         } catch (error) {
       console.error(`❌ Failed to load block ${targetBlock} (DATASET_LOADING_FAILED):`, error);
       this.debugState(`loadCDNBlock: failed for block ${targetBlock}`);
       throw new Error(`Failed to load block ${targetBlock}: ${error instanceof Error ? error.message : String(error)}`);
     }
  }

  // Try-load wrapper that returns null on non-JSON responses or HTML error pages
  private async tryLoadCDNBlock(targetBlock: number): Promise<CDNBlock | null> {
    try {
      const url = `${this.cdnBaseUrl}/${this.config.gameType}/${this.config.gameType}-${this.config.season}-${this.config.language}-block-${targetBlock}.json?v=${Date.now()}`;
      console.log(`🔗 Attempting to load CDN block: ${url}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(url, {
        cache: 'no-cache', // Force fresh fetch to avoid stale cache
        headers: { 'Accept': 'application/json' },
        signal: controller.signal
      });
      
      // Log if we're using cached data (prewarmed at spin start)
      const cacheStatus = response.headers.get('cf-cache-status') || response.headers.get('x-cache') || 'unknown';
      console.log(`📦 Block ${targetBlock} cache status: ${cacheStatus} (prewarmed at spin start)`);
      
      clearTimeout(timeoutId);
      
      console.log(`📡 Block ${targetBlock} response status: ${response.status} ${response.statusText}`);
      if (this.verbose) {
        console.log(`📡 Block ${targetBlock} response headers:`, Object.fromEntries(response.headers.entries()));
      }
      
      if (!response.ok) {
        console.error(`❌ Block ${targetBlock} failed: HTTP ${response.status} ${response.statusText}`);
        this.notifySupport(targetBlock, `HTTP ${response.status} ${response.statusText}`, url);
        return null;
      }
      
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        console.error(`❌ Block ${targetBlock} failed: Invalid content-type: ${contentType}`);
        this.notifySupport(targetBlock, `Invalid content-type: ${contentType}`, url);
        return null;
      }
      
      const block: CDNBlock = await response.json();
      console.log(`✅ Block ${targetBlock} loaded successfully: ${block.questions?.length || 0} questions`);
      // Do NOT update currentBlockNumber during prefetch/try-load; only set when committing the set
      return block;
    } catch (error) {
      console.error(`❌ Block ${targetBlock} failed: Network/parse error:`, error);
      this.notifySupport(targetBlock, 'Network/parse error', `${this.cdnBaseUrl}/${this.config.gameType}/${this.config.gameType}-${this.config.season}-${this.config.language}-block-${targetBlock}.json`);
      return null;
    }
  }

  private notifySupport(blockNumber: number, reason: string, url?: string): void {
    // Completely disabled email notifications to avoid CSP errors
    // Email notifications are not critical for main functionality
    return;
  }

  /**
   * Get next block number with randomization
   */
  private getNextBlockNumber(): number {
    if (this.verbose) console.log(`🎯 getNextBlockNumber: totalBlocks=${this.totalBlocks}, currentBlockNumber=${this.currentBlockNumber}, sessionRng=${this.sessionRng ? 'initialized' : 'null'}, sessionStride=${this.sessionStride}`);
    
    if (this.totalBlocks === 0) {
      if (this.verbose) console.log(`🎯 getNextBlockNumber: returning 1 (no totalBlocks)`);
      return 1;
    }
    
    // Deterministic cursor: iterate blocks sequentially
    if (this.currentBlockNumber <= 0) {
      // Use a seeded start block on first selection to avoid always starting at block 1
      let start = this.computeSeededStartBlock();
      // Skip known invalid blocks
      let guard = 0;
      while (this.invalidBlocks.has(start) && guard < this.totalBlocks) {
        start = (start % this.totalBlocks) + 1;
        guard++;
      }
      if (this.verbose) console.log(`🎯🎯🎯 FIRST BLOCK SELECTED! Seeded: ${this.computeSeededStartBlock()}, Final: ${start}, Stride: ${this.sessionStride}, Invalid Blocks: ${Array.from(this.invalidBlocks)}`);
      return start;
    }
    
    // Advance by session stride to diversify block order (still deterministic within session)
    let next = (this.currentBlockNumber - 1 + this.sessionStride) % this.totalBlocks; // 0..totalBlocks-1
    let candidate = next + 1;
    
    if (this.verbose) console.log(`🎯 Block calculation: current=${this.currentBlockNumber}, stride=${this.sessionStride}, next=${next}, candidate=${candidate}`);
    
    // Add some randomization to avoid always following the same pattern
    if (this.sessionRng && this.sessionRng() < 0.3) { // 30% chance to add extra randomization
      const randomOffset = Math.floor(this.sessionRng() * 5) + 1; // 1-5 blocks offset
      candidate = ((candidate - 1 + randomOffset) % this.totalBlocks) + 1;
      if (this.verbose) console.log(`🎲 Adding random offset: +${randomOffset}, new candidate=${candidate}`);
    }
    
    // Skip known invalid blocks
    let guard = 0;
    while (this.invalidBlocks.has(candidate) && guard < this.totalBlocks) {
      next = (next + 1) % this.totalBlocks;
      candidate = next + 1;
      guard++;
    }
    
    if (this.verbose) console.log(`🔄 Block advancement: current=${this.currentBlockNumber}, stride=${this.sessionStride}, next=${candidate}, invalidBlocks=${Array.from(this.invalidBlocks)}`);
    return candidate;
  }

  /**
   * Select diverse questions ensuring category balance
   */
  private selectDiverseQuestions(questions: Question[]): Question[] {
    const targetCount = this.config.questionsPerSet;
    const selected: Question[] = [];
    const categoryCounts: Map<string, number> = new Map();
    
    // Shuffle questions with session RNG for per-session diversity
    const shuffled = this.shuffleArray([...questions], this.sessionRng || undefined);
    
    for (const question of shuffled) {
      if (selected.length >= targetCount) break;
      
      const currentCategoryCount = categoryCounts.get(question.category) || 0;
      const maxPerCategory = Math.ceil(targetCount / 3); // Ensure diversity
      
      if (currentCategoryCount < maxPerCategory) {
        selected.push(question);
        categoryCounts.set(question.category, currentCategoryCount + 1);
      }
    }
    
    // If we don't have enough diverse questions, fill with remaining
    if (selected.length < targetCount) {
      for (const question of shuffled) {
        if (selected.length >= targetCount) break;
        if (!selected.some(q => q.id === question.id)) {
          selected.push(question);
        }
      }
    }
    
    // Final shuffle to randomize order
    return this.shuffleArray(selected, this.sessionRng || undefined);
  }

  /**
   * Fisher-Yates shuffle algorithm
   */
  private shuffleArray<T>(array: T[], rng?: () => number): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const r = rng ? rng() : Math.random();
      const j = Math.floor(r * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Load used question IDs from localStorage
   */
  private loadUsedQuestionIds(): void {
    try {
      const key = `usedQuestions_${this.config.gameType}_${this.config.season}_${this.config.language}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        this.usedQuestionIds = new Set(JSON.parse(stored).map((id: number) => String(id)));
        if (this.verbose) {
          console.log(`📋 Loaded ${this.usedQuestionIds.size} used question IDs`);
        }
      }
      // Restore current block for anonymous users
      const blockKey = `currentBlock_${this.config.gameType}_${this.config.season}_${this.config.language}`;
      const storedBlock = localStorage.getItem(blockKey);
      if (storedBlock) {
        const parsed = parseInt(storedBlock, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
          this.currentBlockNumber = parsed;
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to load used question IDs:', error);
      this.usedQuestionIds = new Set();
    }
  }

  /**
   * Save used question IDs to localStorage
   */
  private saveUsedQuestionIds(): void {
    try {
      const key = `usedQuestions_${this.config.gameType}_${this.config.season}_${this.config.language}`;
      const data = Array.from(this.usedQuestionIds).map(id => Number(id));
      localStorage.setItem(key, JSON.stringify(data));
      // Also persist current block for anonymous users
      const blockKey = `currentBlock_${this.config.gameType}_${this.config.season}_${this.config.language}`;
      localStorage.setItem(blockKey, String(this.currentBlockNumber));
    } catch (error) {
      console.warn('⚠️ Failed to save used question IDs:', error);
    }
  }

  /**
   * Reset used questions (for new game session)
   */
  resetUsedQuestions(): void {
    this.usedQuestionIds.clear();
    this.saveUsedQuestionIds();
    console.log('🔄 Reset used questions');
  }

  /**
   * Static method to clear all used questions for all game types
   * This fixes the issue where different games start with different question counts
   */
  static clearAllUsedQuestions(): void {
    try {
      const gameTypes = ['trivia', 'wouldYouRather', 'whichOneYouAre', 'truth', 'dare'];
      const languages = ['en', 'es', 'fr', 'hi', 'de'];
      const seasons = ['v1'];
      
      let clearedCount = 0;
      
      for (const gameType of gameTypes) {
        for (const language of languages) {
          for (const season of seasons) {
            const key = `usedQuestions_${gameType}_${season}_${language}`;
            const blockKey = `currentBlock_${gameType}_${season}_${language}`;
            
            if (localStorage.getItem(key)) {
              localStorage.removeItem(key);
              clearedCount++;
            }
            if (localStorage.getItem(blockKey)) {
              localStorage.removeItem(blockKey);
            }
          }
        }
      }
      
      console.log(`🗑️ Cleared used questions for ${clearedCount} game configurations`);
    } catch (error) {
      console.warn('⚠️ Failed to clear all used questions:', error);
    }
  }

  /**
   * Handle CDN index lastUpdated - if content changed, clear local used IDs to surface new content
   */
  private handleIndexLastUpdated(lastUpdated: string): void {
    try {
      const k = `indexLastUpdated_${this.config.gameType}_${this.config.season}_${this.config.language}`;
      const prev = localStorage.getItem(k);
      if (prev && prev !== lastUpdated) {
        console.log('🔃 CDN content updated, clearing local used questions for fresh experience');
        this.usedQuestionIds.clear();
        this.saveUsedQuestionIds();
      }
      localStorage.setItem(k, lastUpdated);
    } catch {
      // ignore
    }
  }

  /**
   * Get statistics about current state
   */
  getStats(): {
    currentQuestions: number;
    usedQuestions: number;
    prefetchedQuestions: number;
    currentBlock: number;
    totalBlocks: number;
    nextPrefetchBlock: number | null;
    prefetchAt: number;
  } {
    return {
      currentQuestions: this.currentQuestions.length,
      usedQuestions: this.usedQuestionIds.size,
      prefetchedQuestions: this.prefetchedQuestions.length,
      currentBlock: this.currentBlockNumber,
      totalBlocks: this.totalBlocks,
      nextPrefetchBlock: this.prefetchedBlockNumber,
      prefetchAt: this.config.prefetchAtQuestion
    };
  }

  /**
   * Force reload questions from CDN
   */
  async forceReload(): Promise<Question[]> {
    this.usedQuestionIds.clear();
    this.saveUsedQuestionIds();
    return this.loadNextQuestionSet();
  }


}

// Factory function for easy creation
export function createGameQuestionManager(
  gameType: GameConfig['gameType'],
  language: GameConfig['language'] = 'en',
  season: GameConfig['season'] = 'v1',
  questionsPerSet: number = 10,
  prefetchAtQuestion: number = 5
): GameQuestionManager {
  return new GameQuestionManager({
    gameType,
    language,
    season,
    questionsPerSet,
    prefetchAtQuestion
  });
}
